/**
 * 把 WSClient 的原始帧路由到各 Pinia store, 由 App.vue 在登录后启动。
 *
 * 单例的副作用注册一次即可(用 module-scope flag 防重复)。
 *
 * 当前接入的帧:
 * - auth_ok        : 仅 console (可选 toast); 真正的 token / me 由 REST 走完
 * - lobby_state    : lobby.applyLobbyState  (整体替换 baseline)
 * - lobby_event    : lobby.applyLobbyEvent  (增量 patch); kind=notification → toast
 * - room_state     : room.applyRoomState    (整体替换 baseline)
 * - event          : room.recordEvent + animBus.emit (双轨: store 记录 + 动画总线分发)
 * - error          : toast + 若 code=bad_token 则强制 logout
 * - kicked         : toast + 兜底 reset (lobby_event 会同步推到 me.room_id=null)
 * - left_room      : 仅 console (room.reset 由 me.room_id watcher 兜底)
 */

import { watch } from 'vue'

import { wsClient } from '@/ws/socket'
import { useAuthStore } from '@/stores/auth'
import { useLobbyStore } from '@/stores/lobby'
import { useRoomStore } from '@/stores/room'
import { useChatStore } from '@/stores/chat'
import { useNotificationsStore } from '@/stores/notifications'
import { useAnimationBus } from '@/composables/useAnimationBus'
import { useAnimationStore } from '@/stores/animations'

import type { ChatMessage } from '@/ws/frames'
import { useSound } from '@/composables/useSound'

let installed = false

export function installWebSocket(): void {
  if (installed) return
  installed = true

  const auth  = useAuthStore()
  const lobby = useLobbyStore()
  const room  = useRoomStore()
  const chat  = useChatStore()
  const notif = useNotificationsStore()
  const animBus = useAnimationBus()
  const animStore = useAnimationStore()

  // ── 帧路由 ────────────────────────────────────────────────
  wsClient.on('auth_ok', (frame) => {
    console.info('[ws] auth_ok', frame.pid, frame.name)
  })

  wsClient.on('lobby_state', (frame) => {
    lobby.applyLobbyState(frame)
  })

  wsClient.on('lobby_event', (frame) => {
    lobby.applyLobbyEvent(frame)
    if (frame.kind === 'notification') {
      const text = (frame.data?.text as string) || ''
      if (text) notif.info(text)
    }
  })

  wsClient.on('room_state', (frame) => {
    room.applyRoomState(frame)
    // M10: chat 历史跟着 snapshot 走 (重连/切房自动对齐).
    chat.applyHistory(frame.chat_history)
  })

  wsClient.on('event', (frame) => {
    // 双轨: store 记录最近一条 (供调试 / 兜底), bus 广播给 AnimationLayer 等订阅者。
    room.recordEvent(frame)
    animBus.emit(frame.kind, frame.data)
    if (frame.kind === 'chat_message') {
      const msg = frame.data as unknown as ChatMessage
      chat.addIncoming(msg)
      const { play } = useSound()
      const PRESET_SOUNDS: Record<string, Parameters<typeof play>[0]> = {
        '给阿姨倒一杯卡布奇诺': 'chat_kapuchino',
        '给我擦皮鞋':           'chat_capixie',
        '我要验牌':             'chat_yanpai',
        '原来是小瘪三':         'chat_xiaobiesan',
        '我嚟同你玩':           'chat_wolaitongniwan',
        '蚕食！':               'chat_canshi',
        '蛰伏！':               'chat_zhefu',
      }
      const sfx = PRESET_SOUNDS[msg.message]
      if (sfx) play(sfx)
    }
    if (frame.kind === 'react') {
      const d = frame.data as { from_pid: string; target_pid: string; emoji: string }
      animStore.emitReact(d.from_pid, d.target_pid, d.emoji)
    }
  })

  wsClient.on('kicked', (frame) => {
    notif.error(frame.reason || '你被移出了房间')
    room.reset()
    chat.reset()
    // me.room_id 在 lobby_event 里会被推到 null, useRouteSync 把路由跳回大厅。
  })

  wsClient.on('left_room', () => {
    // 与 kicked 类似, 真相走 lobby_event → me.room_id → useRouteSync。
    room.reset()
    chat.reset()
  })

  wsClient.on('error', (frame) => {
    const friendly: Record<string, string> = {
      bad_token:     '登录已过期, 请重新登录',
      missing_token: '缺少登录凭证',
      no_player:     '账号已被删除',
    }
    notif.error(friendly[frame.code] || frame.msg || frame.code)
    if (frame.code === 'bad_token' || frame.code === 'no_player') {
      auth.logout()
      lobby.reset()
      wsClient.disconnect()
    }
  })

  // ── token 变化驱动连接生命周期 ────────────────────────────
  watch(
    () => auth.token,
    (token, prev) => {
      if (token && token !== prev) {
        wsClient.connect(token)
      } else if (!token && prev) {
        wsClient.disconnect()
        lobby.reset()
      }
    },
    { immediate: true },
  )
}

/** 让组件可以读到当前 WS 状态 (用于 banner 等)。*/
export function useWebSocketStatus() {
  return wsClient.status
}
